import { RegularTextSmall } from "../../../../components/Typography/Typography";

import { useTranslation } from "react-i18next";
import { H3 } from "../../../../components/Typography/Typography";
import styled from "styled-components";
import {
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../components/Buttons/Buttons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useStoreState } from "../../../../util/util";
import axios, { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { FilePickerUncontrolled } from "../../../../components/FilePicker/FilePickerUncontrolled";
import { GenericDialogBody } from "../../../../components/ConfirmDialog/ConfirmDialog";
import { UploadingCSVModalBody } from "./UploadingCSVModalBody";
import { StringParam, useQueryParams } from "use-query-params";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import { useLocation } from "react-router-dom";
import { useRoutePath } from "../../../../util/Routing";
import { spacingTokens } from "../../../../designTokens";
import { useMdmEntityDisplayNames } from "../../../../util/mdmEntityDisplayNames";
import { BulkUploadCustomerType } from "../../../../types/types.MDM";
import { bulkUploadEntityType } from "./BulkUploadCustomers";

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
`;

const UploadStageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_5xl")};
  max-width: 512px;
`;

export const UploadStage = ({
  updateStage,
  customer_type,
}: {
  updateStage: (
    stage: "download" | "upload",
    uploadNumber?: string,
    shouldMutate?: boolean
  ) => void;
  customer_type: BulkUploadCustomerType;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const { t } = useTranslation();
  const { storefront_id, tenant_id } = useStoreState();
  const { getLabel } = useMdmEntityDisplayNames();
  const [query] = useQueryParams({
    uploadNumber: StringParam,
  });
  const methodsOfUseForm = useForm();
  const { notifySuccess, notifyError, pollNotification } = useNotifications();
  const location = useLocation();
  const { adminPath } = useRoutePath();
  const entityType = bulkUploadEntityType(customer_type);
  const singular = getLabel(entityType, "singular");
  const plural = getLabel(entityType, "plural");

  const getPollFn = useCallback(
    (uploadNumber: string) => {
      const pollFn = async () => {
        const {
          data: { result, status },
        } = await axios.get<{
          status: "queued" | "started" | "deferred" | "finished" | "failed";
          result?: { errors: object[]; success: boolean };
        }>(
          `/v2/storefronts/${storefront_id}/tenants/${tenant_id}/bulk_upload/${uploadNumber}/status`
        );
        const { success } = result ?? { success: false, errors: [] };
        const message = success
          ? t("{{entities}} have been created successfully", {
              entities: plural,
            })
          : t("There was an error creating {{entities}}", {
              entities: plural,
            });
        const updated_status = success ? "success" : "failed";
        return {
          status: updated_status,
          shouldPoll: !result || !["finished", "failed"].includes(status),
          message,
          uploadNumber,
        };
      };
      return pollFn;
    },
    [storefront_id, t, tenant_id, plural]
  );

  const isInUploadPage = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const path =
      customer_type === "customers"
        ? "customers/create"
        : "distributors/create";
    return (
      location.pathname === `${adminPath}/${path}` &&
      searchParams.get("tab") === "1"
    );
  }, [location.search, location.pathname, adminPath, customer_type]);

  const onCompletePolling = useCallback(
    (pollResult: { status: string; message: string; uploadNumber: string }) => {
      if (isInUploadPage) {
        setShowUploadDialog(false);
        updateStage("download", undefined, true);
      }
      if (pollResult.status === "success") {
        notifySuccess(pollResult.message);
      } else {
        notifyError(pollResult.message);
      }
    },
    [isInUploadPage, notifyError, notifySuccess, updateStage]
  );

  const handleFile = (file: File) => {
    setSelectedFile(file);
  };

  const onUpload = async () => {
    if (selectedFile) {
      if (!query.uploadNumber) {
        const formData = new FormData();
        const customer_type_param =
          customer_type === "customers" ? "Buyer" : "Distributor";
        formData.append("file", selectedFile);
        try {
          const {
            data: { job_id },
          } = await axios.post<{ job_id: string }>(
            `/v2/storefronts/${storefront_id}/tenants/${tenant_id}/bulk_upload?type=${customer_type_param}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          updateStage("upload", job_id);
          setShowUploadDialog(true);
          pollNotification({
            pollFn: getPollFn(job_id),
            onComplete: onCompletePolling,
          });
        } catch (error) {
          const errorMessage = (error as AxiosError)?.response?.data?.message;
          notifyError(
            errorMessage ?? t("There was an error uploading the file"),
            { error }
          );
        }
      } else {
        setShowUploadDialog(true);
        pollNotification({
          pollFn: getPollFn(query.uploadNumber),
          onComplete: onCompletePolling,
        });
      }
    }
  };
  const goBack = () => {
    updateStage("download");
  };

  useEffect(() => {
    if (query.uploadNumber && !selectedFile) {
      setShowUploadDialog(true);
      pollNotification({
        pollFn: getPollFn(query.uploadNumber),
        onComplete: onCompletePolling,
      });
    }
  }, [
    getPollFn,
    onCompletePolling,
    pollNotification,
    query.uploadNumber,
    selectedFile,
  ]);

  return (
    <UploadStageContainer>
      <TextSection>
        <H3 style={{ margin: 0 }}>
          {t("Upload {{entity}} Data", { entity: singular })}
        </H3>
        <RegularTextSmall style={{ margin: 0 }}>
          {t(
            "The following CSV files contains the headers of required data for {{entity}} creation. Download the file, update with {{entities}} information and upload.",
            { entity: singular, entities: plural }
          )}
        </RegularTextSmall>
      </TextSection>
      <FilePickerUncontrolled
        methodsOfUseForm={methodsOfUseForm}
        name="file"
        handleFile={handleFile}
        accept={".csv"}
        required={true}
      />
      <div style={{ display: "flex", gap: spacingTokens.spacing_xl }}>
        <TertiaryButtonLargeV2 style={{ maxWidth: "70px" }} onClick={goBack}>
          {t("Back")}
        </TertiaryButtonLargeV2>
        <PrimaryButtonLargeV2
          disabled={!selectedFile}
          style={{ maxWidth: "100px" }}
          onClick={onUpload}
        >
          {t("Upload")}
        </PrimaryButtonLargeV2>
      </div>
      <GenericDialogBody
        show={showUploadDialog}
        closeDialog={() => setShowUploadDialog(false)}
      >
        <UploadingCSVModalBody />
      </GenericDialogBody>
    </UploadStageContainer>
  );
};
